import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { combine, createStore, Store } from 'effector';
import { RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { createPaginatedSchema, DeviceDetailed, deviceDetailedSchema } from '../types';

export type OriginalDeviceRegistryParams = {
	sortField?: string | null;
	sortDirection?: string;
	page?: number;
	limit?: number;
	name?: string;
	cashdeskCode?: string;
	accountIds?: string[];
	gameIds?: string[];
	cashdeskIds?: string[];
	currencies?: string[];
	isActive?: string[];
};

export type DeviceRegistryFilters = {
	name: string;
	cashdeskCode: string;
	accountIds: number[];
	gameIds: number[];
	cashdeskIds: number[];
	currencies: string[];
	isActive: boolean[];
};

export type DeviceRegistryParams = RegistryParams<DeviceRegistryFilters>;

export type DeviceRegistryQuery = RegistryQuery<
	DeviceDetailed,
	DeviceRegistryFilters,
	JsonApiRequestError
>;

export const createDeviceRegistryQuery = (
	$accountId?: Store<number | null>,
	$cashdeskId?: Store<number | null>,
): DeviceRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<DeviceRegistryParams>(),
		request: {
			method: 'GET',
			url: '/device/list',
			query: {
				source: combine([
					$accountId ?? createStore<number | null>(null),
					$cashdeskId ?? createStore<number | null>(null),
				]),
				fn: (params, [accountId, cashdeskId]): OriginalDeviceRegistryParams => ({
					page: params.page,
					limit: params.limit,
					sortField: params.sortField ?? undefined,
					sortDirection: params.sortDirection,
					name: params.filters.name ?? undefined,
					cashdeskCode: params.filters.cashdeskCode ?? undefined,
					accountIds: [
						...(accountId ? [String(accountId)] : []),
						...params.filters.accountIds.map(String),
					],
					gameIds: params.filters.gameIds.map(String),
					cashdeskIds: [
						...(cashdeskId ? [String(cashdeskId)] : []),
						...params.filters.cashdeskIds.map(String),
					],
					currencies:
						params.filters.currencies?.length === 0 ? undefined : params.filters.currencies,
					isActive:
						params.filters.isActive.length === 0 ? undefined : params.filters.isActive.map(String),
				}),
			},
		},
		response: {
			contract: zodContract(createPaginatedSchema(deviceDetailedSchema)),
		},
	});

	return query;
};
